import React, { useEffect, useState, useRef } from "react";
import { manchiApi } from "@/lib/manchiApi";
import Globe from "@/pages/manchi/Globe";
import { Loader2, X, Globe2, Info, Newspaper } from "lucide-react";
import { toast } from "sonner";

export default function ManchiWorld() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [peek, setPeek] = useState(null);
  const peekTimer = useRef(null);
  const peekCache = useRef({});

  useEffect(() => {
    manchiApi.get("/config").then(({ data }) => setCountries(data.countries || []))
      .catch(() => toast.error("Couldn't load countries"))
      .finally(() => setLoading(false));
  }, []);

  const patchCountries = async (next) => {
    setSaving(true);
    try { await manchiApi.patch("/config", { countries: next }); }
    catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  const toggle = (name) => {
    const next = countries.includes(name) ? countries.filter((c) => c !== name) : [...countries, name];
    setCountries(next);
    patchCountries(next);
  };

  const onHover = (name, x, y) => {
    clearTimeout(peekTimer.current);
    if (!name) { setPeek(null); return; }
    setPeek({ country: name, x, y, loading: !peekCache.current[name], ...(peekCache.current[name] || {}) });
    if (peekCache.current[name]) return;
    peekTimer.current = setTimeout(async () => {
      try {
        const { data } = await manchiApi.get("/peek", { params: { country: name } });
        peekCache.current[name] = { title: data.title, source: data.source, url: data.url };
      } catch {
        peekCache.current[name] = { title: null, source: null, url: null };
      }
      setPeek((p) => (p && p.country === name ? { ...p, loading: false, ...peekCache.current[name] } : p));
    }, 400);
  };

  if (loading) return <div className="text-center py-16 text-rose-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="manchi-world">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-400 flex items-center gap-2">
          <Globe2 className="w-4 h-4" /> your world
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-rose-900 mt-1">Spin the globe, pick your markets.</h1>
        <p className="text-rose-500 text-sm mt-2 flex items-center gap-1.5">
          <Info className="w-4 h-4" /> Drag to rotate · tap a country to include its Economics &amp; Finance news. {saving && <span className="text-pink-500">saving…</span>}
        </p>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start">
        <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6">
          <Globe selected={countries} onToggle={toggle} onHover={onHover} />
          <p className="text-center text-[11px] text-rose-300 mt-3">Hover a country to peek at its top Economics headline.</p>
        </div>

        <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6">
          <div className="text-sm font-bold text-rose-900 mb-3">
            Selected countries <span className="text-rose-400 font-normal">({countries.length})</span>
          </div>
          {countries.length === 0 ? (
            <p className="text-rose-400 text-sm">Tap countries on the globe to start.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="manchi-selected-countries">
              {countries.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 bg-pink-100 text-rose-700 text-xs font-semibold rounded-full pl-3 pr-2 py-1.5">
                  {c}
                  <button onClick={() => toggle(c)} data-testid={`manchi-remove-country-${c.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`} className="hover:bg-pink-200 rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {peek && (
        <div
          data-testid="manchi-peek-tooltip"
          className="fixed z-50 max-w-xs pointer-events-none bg-white border border-pink-200 shadow-xl shadow-pink-300/40 rounded-2xl p-3"
          style={{ left: Math.min(peek.x + 14, window.innerWidth - 300), top: peek.y + 14 }}
        >
          <div className="text-[10px] uppercase tracking-widest text-rose-400 flex items-center gap-1 mb-1">
            <Newspaper className="w-3 h-3" /> {peek.country}
          </div>
          {peek.loading ? (
            <div className="text-rose-400 text-xs flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching headline…</div>
          ) : peek.title ? (
            <div>
              <div className="text-rose-900 text-sm font-semibold leading-snug">{peek.title}</div>
              {peek.source && <div className="text-pink-500 text-[11px] mt-1">{peek.source}</div>}
            </div>
          ) : (
            <div className="text-rose-400 text-xs">No recent Economics headline.</div>
          )}
        </div>
      )}
    </div>
  );
}
